import React, { useEffect, useState } from "react";
import "./CellHistoryDialog.css";
import { toColumnLabel } from "../../utils/columnUtils";

/**
 * CellHistoryDialog — Excel-style "show changes" for a single cell.
 *
 * Lists who changed the cell, when, and each old -> new value, newest
 * first. History entries come from the ExcelWebEdit history endpoint via
 * fetchHistory() (the full document log); this dialog filters to the
 * requested sheet/cell.
 */
export default function CellHistoryDialog({ target, sheet, fetchHistory, onClose }) {
  // target: { row, col } | null
  const [state, setState] = useState({ loading: true, error: "", entries: [] });

  useEffect(() => {
    if (!target) return undefined;
    let cancelled = false;
    setState({ loading: true, error: "", entries: [] });
    fetchHistory()
      .then((all) => {
        if (cancelled) return;
        const entries = all
          .filter((e) => e.sheet === sheet && e.row === target.row && e.col === target.col)
          .sort((a, b) => new Date(b.editedAtUtc) - new Date(a.editedAtUtc));
        setState({ loading: false, error: "", entries });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({ loading: false, error: err.message || "Unable to load history", entries: [] });
      });
    return () => { cancelled = true; };
  }, [target, sheet, fetchHistory]);

  if (!target) return null;
  const cellRef = `${toColumnLabel(target.col)}${target.row + 1}`;

  return (
    <>
      <div className="sev-history-backdrop" onClick={onClose} />
      <div className="sev-history-dialog" role="dialog" aria-label={`Edit history for ${cellRef}`}>
        <div className="sev-history-head">
          <span className="material-icons">history</span>
          <span className="sev-history-title">Edit history — {cellRef} ({sheet})</span>
          <button type="button" className="sev-history-close" onClick={onClose} title="Close">
            <span className="material-icons">close</span>
          </button>
        </div>
        <div className="sev-history-body">
          {state.loading && <div className="sev-history-status">Loading history...</div>}
          {!state.loading && state.error && (
            <div className="sev-history-status sev-history-error">{state.error}</div>
          )}
          {!state.loading && !state.error && state.entries.length === 0 && (
            <div className="sev-history-status">No edits recorded for this cell.</div>
          )}
          {!state.loading && !state.error && state.entries.map((e, i) => (
            <div className="sev-history-entry" key={i}>
              <div className="sev-history-meta">
                <span className="sev-history-who">{e.email}</span>
                <span className="sev-history-when">{new Date(e.editedAtUtc).toLocaleString()}</span>
              </div>
              <div className="sev-history-change">
                <span className="sev-history-old">{e.oldValue === "" ? "(empty)" : e.oldValue}</span>
                <span className="material-icons sev-history-arrow">arrow_forward</span>
                <span className="sev-history-new">{e.newValue === "" ? "(empty)" : e.newValue}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}
